import { getBaseApiUrl } from '../../../config/api';

const getAuthHeaders = () => {
  const token =
    localStorage.getItem('pathbuddy_access_token') ||
    sessionStorage.getItem('pathbuddy_access_token') ||
    localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Emergency passcode sessions send the verified passcode header.
  const storedPasscode =
    sessionStorage.getItem('ivori_admin_passcode') ||
    sessionStorage.getItem('ivori_certadmin_passcode') ||
    localStorage.getItem('ivori_admin_passcode') ||
    localStorage.getItem('ivori_certadmin_passcode') ||
    '';
  if (storedPasscode) {
    headers['X-Admin-Passcode'] = storedPasscode;
  }
  return headers;
};


export const certificateAPI = {
  /**
   * Whether the signed-in user may use the admin pages (admin role or ADMIN_EMAILS).
   */
  async getAdminAccess() {
    try {
      const response = await fetch(`${getBaseApiUrl()}/api/certificates/admin-access`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      return {
        isAdmin: Boolean(response.ok && data.is_admin),
        passcodeEnabled: Boolean(data.passcode_enabled)
      };
    } catch (e) {
      return { isAdmin: false, passcodeEnabled: false };
    }
  },

  /**
   * Verify and fetch a single certificate by ID.
   * Public read-only endpoint.
   */
  async getCertificate(certId) {
    if (!certId) {
      throw new Error('Certificate ID is required');
    }

    const cleanId = encodeURIComponent(certId.trim().toUpperCase());
    const response = await fetch(`${getBaseApiUrl()}/api/certificates/${cleanId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || data.error || 'Certificate not found');
    }

    return data.certificate;
  },

  /**
   * Search registered students (instructor / admin only).
   */
  async searchStudents(query = '', limit = 10) {
    if (!query || query.trim().length < 2) return [];
    try {
      const response = await fetch(
        `${getBaseApiUrl()}/api/certificates/students/search?q=${encodeURIComponent(query.trim())}&limit=${limit}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      return data.students || [];
    } catch (e) {
      // Graceful fallback if offline or unauthorized
      return [];
    }
  },

  /**
   * Issue a new certificate (Instructor / Admin authenticated).
   */
  async issueCertificate(certData) {
    const storedPasscode =
      sessionStorage.getItem('ivori_admin_passcode') ||
      sessionStorage.getItem('ivori_certadmin_passcode') ||
      '';
    const payload = {
      ...certData,
      passcode: certData.passcode || (storedPasscode ? storedPasscode : undefined)
    };

    const response = await fetch(`${getBaseApiUrl()}/api/certificates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || data.error || 'Failed to issue certificate');
    }

    return data;
  },

  /**
   * List all issued certificates for instructor/admin dashboard table.
   */
  async listCertificates(limit = 100) {
    try {
      const response = await fetch(`${getBaseApiUrl()}/api/certificates?limit=${limit}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      return data.certificates || [];
    } catch (e) {
      return [];
    }
  },

  /**
   * List all certificates belonging to a specific user (Tier 1 Membership + Tier 2 Event certificates).
   */
  async getUserCertificates(userId) {
    if (!userId) return [];
    try {
      const response = await fetch(`${getBaseApiUrl()}/api/certificates/user/${userId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (!response.ok) {
        return [];
      }
      return data.certificates || [];
    } catch (e) {
      return [];
    }
  },

  /**
   * Automatically issue/sync certificates for all paid users in the database.
   */
  async syncPaidUsers() {
    try {
      const response = await fetch(`${getBaseApiUrl()}/api/certificates/admin/sync-paid-users`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      return data;
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  /**
   * Automatically issue/sync certificates for all Experience Week (Loizou & Co) cohort students.
   */
  async syncExperienceWeek() {
    try {
      const response = await fetch(`${getBaseApiUrl()}/api/certificates/admin/sync-experience-week`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      return data;
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  /**
   * Trigger automated batch email send for all unsent members, staff and experience week students.
   */
  async sendAllUnsentEmails() {
    try {
      const response = await fetch(`${getBaseApiUrl()}/api/certificates/admin/send-all-unsent`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      return data;
    } catch (e) {
      return { success: false, error: e.message };
    }
  },

  /**
   * Dispatch certificate email via Resend with masked hyperlinks.
   */
  async sendCertificateEmail(certIds = null) {
    try {
      const response = await fetch(`${getBaseApiUrl()}/api/certificates/admin/send-email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(certIds ? { cert_ids: Array.isArray(certIds) ? certIds : [certIds] } : {})
      });
      const data = await response.json();
      return data;
    } catch (e) {
      return { success: false, error: e.message };
    }
  }
};




